import request from "../vendors/request";
import AbstractComponent from "./abstract-component";
import { RevealObserver, animateRevealNodes } from "../utils/reveal";

export default class ArticleList extends AbstractComponent {

    constructor(el) {
        super(el);
        this.DOM = {};
        this._cardObserver = new RevealObserver({
            threshold: 0.15,
            rootMargin: '0px 0px -10% 0px',
        });
        this.request = null;
        this.class = "c-articles-list";
        this.action = "filter_articles_ajax";
        this.params = {
            service: null,
            paged: 1
        };
        this.lastFetch = null;
        this.initDomElements();
        this.initPreselectedFilters();
        this.initEvents();
    }

    initDomElements() {
        this.DOM.column = this.$el.querySelector('.' + this.class + '__list');
        this.DOM.noPostMsg = this.$el.querySelector('.no-post');
        this.DOM.moreBtn = this.$el.querySelector('.moreBtn');
        this.DOM.selects = this.$el.querySelectorAll('select');
        this.DOM.resetBtn = this.$el.querySelector('.reset');

        const initialCards = this.DOM.column ? Array.from(this.DOM.column.querySelectorAll('.c-article-card')) : [];
        this._observeCards(initialCards);
    }

    _observeCards(nodes) {
        this._cardObserver.observe(nodes);
    }

    initPreselectedFilters() {
        this.DOM.selects.forEach(select => {
            const filterName = select.getAttribute('name');
            const filterValue = select.value;

            if (filterValue && filterValue !== '' && filterValue !== 'all') {
                this.params[filterName] = filterValue;
            }
        });

        const hasActiveFilters = this.params.service;
        if (hasActiveFilters && this.DOM.resetBtn) {
            this.app.gsap.set(this.DOM.resetBtn, { display: 'flex', autoAlpha: 1, x: 0 });
        } else if (this.DOM.resetBtn) {
            this.app.gsap.set(this.DOM.resetBtn, { display: 'none', autoAlpha: 0, x: -5 });
        }
    }

    initEvents() {
        if (this.DOM.moreBtn) {
            this.DOM.moreBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.getNextPage();
            });
        }
        if (this.DOM.resetBtn) {
            this.DOM.resetBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.resetFilters()
            });
        }
        this.DOM.selects.forEach(select => {
            select.addEventListener('change', () => {
                const filterName = select.getAttribute('name');
                const filterValue = select.value;

                this.lastFetch = 'filter'
                this.params[filterName] = filterValue;

                const hasActiveFilters = this.params.service;

                if (this.DOM.resetBtn) {
                    if (!hasActiveFilters) {
                        this.app.gsap.fromTo(this.DOM.resetBtn, { autoAlpha: 1, x: 0 }, {
                            autoAlpha: 0, x: -5, onComplete: () => {
                                this.app.gsap.set(this.DOM.resetBtn, { display: 'none' })
                            }
                        })
                    } else {
                        this.app.gsap.set(this.DOM.resetBtn, { display: 'flex' })
                        this.app.gsap.fromTo(this.DOM.resetBtn, { autoAlpha: 0, x: 5 }, {
                            autoAlpha: 1, x: 0
                        })
                    }
                }
                this.params.paged = 1;
                this.fetchArticles();
            });
        });
    }

    updateUrlParams() {
        const urlParams = new URLSearchParams();
        if (this.params.service) {
            urlParams.set('service', this.params.service);
        }
        const newUrl = `${window.location.pathname.replace(/\/page\/\d+\//, '/')}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        history.pushState({}, '', newUrl);
    }

    getNextPage() {
        this.params.paged++;
        this.lastFetch = 'next';
        this.fetchArticles();
    }

    fetchArticles() {
        if (this.request && this.request.abort) {
            try { this.request.abort(); } catch (e) { }
        }
        
        this.updateUrlParams();
        if (this.DOM.moreBtn) {
            this.DOM.moreBtn.classList.add('is-loading');
        }

        this.request = request.AJAX({
            'url': window.JWP.ajax_url + "?action=" + this.action,
            'data': this.params,
            'success': this.onSuccess.bind(this)
        });
    }

    resetFilters() {
        this.params.service = null;
        this.DOM.selects.forEach(select => {
            select.value = select.options[0].value
        })
        this.params.paged = 1;
        this.lastFetch = 'filter';
        this.fetchArticles();
        if (this.DOM.resetBtn) {
            this.app.gsap.fromTo(this.DOM.resetBtn, { autoAlpha: 1, x: 0 }, {
                autoAlpha: 0, x: -5, onComplete: () => {
                    this.app.gsap.set(this.DOM.resetBtn, { display: 'none' })
                }
            })
        }
    }

    animateNewCards(nodes, stagger = 0.06) {
        animateRevealNodes(nodes, { stagger });
    }

    onSuccess(response) {
        const data = response.data;

        if (this.lastFetch === 'filter') this.clearColumn();

        if (data.articles && data.articles.length) {
            data.articles.forEach(a => {
                this.DOM.column.insertAdjacentHTML('beforeend', a.html);
            });

            const cards = Array.from(this.DOM.column.querySelectorAll('.c-article-card'));
            const lastBatch = cards.slice(-data.articles.length);
            this._observeCards(lastBatch);

            this.DOM.moreBtn?.classList.remove('is-loading');
            setTimeout(() => this.app.$scroll?.update?.(), 100);
        }

        this.app.gsap.set(this.DOM.noPostMsg, { display: !data.articles?.length && this.lastFetch === "filter" ? 'block' : 'none' });
        if (this.DOM.moreBtn) {
            if (data.has_more) {
                this.app.gsap.set(this.DOM.moreBtn, { display: 'flex' });
                this.app.gsap.fromTo(this.DOM.moreBtn, { y: 4, autoAlpha: 0.9 }, { y: 0, autoAlpha: 1, duration: 0.3, ease: "power2.out" });
            } else {
                this.app.gsap.to(this.DOM.moreBtn, {
                    autoAlpha: 0, duration: 0.2, ease: "power2.out",
                    onComplete: () => this.app.gsap.set(this.DOM.moreBtn, { display: 'none', clearProps: 'opacity,transform' })
                });
            }
        }
    }

    clearColumn() {
        this.DOM.column.innerHTML = '';
    }

    destroy() {
        this._cardObserver.disconnect();
    }
}
